import { useState, useRef } from 'react';
import './license-upload.scss';

interface LicenseUploadProps {
  onImageUploaded: (file: File) => void | Promise<void>;
  language?: 'ar' | 'en' | 'ur';
}

export default function LicenseUpload({ onImageUploaded, language = 'ar' }: LicenseUploadProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const texts = {
    ar: {
      title: 'رفع صورة رخصة المركبة',
      subtitle: 'يرجى رفع صورة واضحة للجهة الأمامية من رخصة المركبة',
      button: 'اختر صورة',
      uploaded: 'تم الرفع',
      change: 'تغيير الصورة',
      instructions: 'تأكد من وضوح البيانات التالية في الصورة:',
      instruction1: 'رقم اللوحة ولونها',
      instruction2: 'مكان الإصدار',
      instruction3: 'صنف اللوحة',
    },
    en: {
      title: 'Upload Vehicle License',
      subtitle: 'Please upload a clear image of the front of the vehicle license',
      button: 'Choose Image',
      uploaded: 'Uploaded',
      change: 'Change Image',
      instructions: 'Make sure the following data is clear in the image:',
      instruction1: 'Plate number and color',
      instruction2: 'Place of issue',
      instruction3: 'Plate category',
    },
    ur: {
      title: 'گاڑی کا لائسنس اپ لوڈ کریں',
      subtitle: 'براہ کرم گاڑی کے لائسنس کے سامنے کی واضح تصویر اپ لوڈ کریں',
      button: 'تصویر منتخب کریں',
      uploaded: 'اپ لوڈ ہو گیا',
      change: 'تصویر تبدیل کریں',
      instructions: 'یقینی بنائیں کہ تصویر میں درج ذیل ڈیٹا واضح ہے:',
      instruction1: 'پلیٹ نمبر اور رنگ',
      instruction2: 'جاری کرنے کی جگہ',
      instruction3: 'پلیٹ کی قسم',
    }
  };

  const t = texts[language];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      const message = language === 'ar' 
        ? 'يرجى اختيار ملف صورة فقط'
        : language === 'ur'
        ? 'براہ کرم صرف تصویر کی فائل منتخب کریں'
        : 'Please select an image file only';
      alert(message);
      return;
    }

    setUploadedFile(file);

    // Generate preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setPreview(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);

    // Notify parent
    onImageUploaded(file);
  };

  const handleChangeImage = () => {
    setUploadedFile(null);
    setPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="license-upload-container">
      <div className="license-upload-header">
        <h3>{t.title}</h3>
        <p className="subtitle">{t.subtitle}</p>
      </div>

      <div className="instructions-box">
        <span className="material-symbols-outlined">info</span>
        <div className="instructions-text">
          <p className="instructions-title">{t.instructions}</p>
          <ul>
            <li>{t.instruction1}</li>
            <li>{t.instruction2}</li>
            <li>{t.instruction3}</li>
          </ul>
        </div>
      </div>

      {!uploadedFile ? (
        <button 
          className="upload-button"
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="material-symbols-outlined">add_photo_alternate</span>
          {t.button}
        </button>
      ) : (
        <div className="uploaded-section">
          <div className="preview-container">
            <img src={preview} alt="License Preview" />
            <div className="upload-success">
              <span className="material-symbols-outlined">check_circle</span>
              <span>{t.uploaded}</span>
            </div>
          </div>
          <button 
            className="change-button"
            onClick={handleChangeImage}
          >
            <span className="material-symbols-outlined">sync</span>
            {t.change}
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}

